/**
 * ExecutiveCp10Aggregates — escalation count, average timing band, and
 * RULE-FORECAST-1 forecast-category distribution for the executive view.
 *
 * Locked by:
 *   - RULE_FORECAST_1.md (category eligibility floor)
 *   - RULE_ESC_1.md       (72h escalation chain)
 *   - S-CP10-4            (Timing Defense Gate)
 */
import { useMemo } from "react";
import { tokens } from "@/components/operator/tokens";
import {
  averageTimingBand,
  categoryColor,
  distribution,
  type DealForecastInput,
  type ForecastCategory,
} from "@/lib/forecast-rules";

interface Props {
  deals: readonly DealForecastInput[];
  escalations: number;
}

// Canon 5-bucket taxonomy — CANON.md lines 167-186.
const CATEGORIES: ForecastCategory[] = ["Commit", "Upside", "Best Case", "Pipeline", "Omit"];

export default function ExecutiveCp10Aggregates({ deals, escalations }: Props) {
  const dist = useMemo(() => distribution(deals), [deals]);
  const total = deals.length;
  const avg = useMemo(() => averageTimingBand(deals.map((d) => d.timing)), [deals]);

  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: 12,
        background: "#FFFFFF",
        padding: 14,
        marginBottom: 16,
      }}
      data-testid="exec-cp10-aggregates"
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: tokens.color.textPrimary,
          letterSpacing: "0.04em",
          marginBottom: 10,
        }}
      >
        CP10 portfolio signals
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 14 }}>
        <Tile label="Active escalations" value={String(escalations)} tone={escalations > 0 ? "danger" : "neutral"} />
        <Tile label="Avg declared timing" value={avg > 0 ? `${avg}%` : "—"} />
        <Tile label="Deals in pipeline" value={String(total)} />
      </div>

      <div style={{ fontSize: 11, color: tokens.color.textMuted, marginBottom: 6, letterSpacing: "0.04em" }}>
        RULE-FORECAST-1 distribution
      </div>
      <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", height: 14, border: `1px solid ${tokens.color.border}` }}>
        {CATEGORIES.map((c) => {
          const v = dist[c];
          const pct = total > 0 ? (v / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={c}
              title={`${c} · ${v} deal${v === 1 ? "" : "s"} (${Math.round(pct)}%)`}
              style={{ width: `${pct}%`, background: categoryColor(c) }}
              data-testid={`dist-${c}`}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
        {CATEGORIES.map((c) => (
          <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: tokens.color.textMuted }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: categoryColor(c) }} />
            {c} <strong style={{ color: tokens.color.textPrimary }}>{dist[c]}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "danger" | "neutral" }) {
  const color = tone === "danger" ? tokens.color.danger : tokens.color.textPrimary;
  return (
    <div style={{ border: `1px solid ${tokens.color.borderSoft}`, borderRadius: 8, padding: "8px 12px" }}>
      <div style={{ fontSize: 10, letterSpacing: "0.08em", color: tokens.color.textMuted, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
